import { DataService } from "@/services/data";
import { ApprenticeshipEnterpriseType } from "@/types/apprenticeship/enterprise";
import { ApprenticeshipProductType } from "@/types/apprenticeship/product";
import { PaginatedResponse } from "@/types/common";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, RouteObject, defer, json, redirect } from "react-router-dom";
import { toast } from "sonner";
import { ApprenticeshipContactType } from "@/types/apprenticeship/contact";

const EnterpriseGroupRoute: RouteObject = {
    path: "groupes",
    action: async ({ request }) => {
        try {
            const data = await request.json();
            const company = (await DataService.client.url(`/apprenticeship/companies/`).post(data).json()) as ApprenticeshipEnterpriseType;
            toast.success("L'entreprise a bien été ajoutée");
            return redirect(`/alternance/groupes/${company.id}`);
        } catch (error) {
            toast.error("Une erreur est survenue lors de l'ajout de l'entreprise");
            return json({ error });
        }
    },
    children: [
        {
            index: true,
            async lazy() {
                let { ListPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/list");
                return { Component: ListPage };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                url.searchParams.set("is_group", "1");
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }

                const search_param = url.searchParams.get("search");
                if (search_param) {
                    url.searchParams.set("smart_search", search_param);
                    url.searchParams.delete("search");
                }

                const companies = DataService.client
                    .url(`/apprenticeship/companies/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return defer({ companies });
            }
        },
        {
            path: "recherche",
            loader: async ({ request }) => {
                const url = new URL(request.url);
                url.searchParams.set("is_group", "1");
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }

                const companies = await DataService.client
                    .url(`/apprenticeship/companies/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json();
                return companies;
            }
        },
        {
            path: "nouveau",
            async lazy() {
                let { NewPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/new");
                return { Component: NewPage };
            },
            loader: async () => {
                const recent_companies = DataService.client.url(`/apprenticeship/companies/recent/`).get().json();
                return defer({ recent_companies });
            },
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    const response = await DataService.client.url(`/apprenticeship/companies/new/`).post(data).json();
                    return json({ success: true, content: response });
                } catch (error) {
                    return handle_error(error);
                }
            }
        },
        {
            id: "apprenticeship-enterprise-group-detail",
            path: ":enterpriseId",
            async lazy() {
                let { DetailLayout } = await import("@/pages/apprenticeship/enterprise/enterprise-group/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const enterpriseId = params.enterpriseId;
                const company = await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/`).get().json();
                const contacts = (await DataService.client.url(`/apprenticeship/contacts/?company_id=${enterpriseId}&page_size=1000`).get().json()) as PaginatedResponse<ApprenticeshipContactType[]>;
                const products = (await DataService.client.url(`/apprenticeship/products/`).get().json()) as PaginatedResponse<ApprenticeshipProductType[]>;
                return defer({ company, contacts: contacts.results, products: products.results });
            },
            action: async ({ params, request }) => {
                const enterpriseId = params.enterpriseId;
                if (request.method == "POST") {
                    try {
                        const data = await request.json();
                        await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/`).patch(data).json();
                        toast.success("Entreprise mise à jour avec succès");
                        return redirect(`/alternance/groupes/${enterpriseId}`);
                    } catch (error) {
                        toast.error("Une erreur est survenue lors de la mise à jour de l'entreprise");
                        return json({ error });
                    }
                }
                if (request.method == "DELETE") {
                    try {
                        await DataService.client.delete(`/apprenticeship/companies/${enterpriseId}/`);
                        toast.success("L'entreprise a bien été supprimée");
                        return redirect(`/alternance/groupes`);
                    } catch (error) {
                        toast.error("Une erreur est survenue lors de la suppression de l'entreprise");
                        return json({ error });
                    }
                }
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="sites" replace />
                },
                {
                    path: "contacts",
                    async lazy() {
                        let { ContactsPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/detail-contacts");
                        return { Component: ContactsPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const contacts = await DataService.client.url(`/apprenticeship/contacts/?company_id=${enterpriseId}&page_size=1000`).get().json();
                        return defer({ contacts });
                    },
                    action: async ({ request, params }) => {
                        const enterpriseId = params.enterpriseId;
                        const data = await request.json();
                        if (request.method == "POST") {
                            try {
                                const payload = {
                                    ...data,
                                    company: { id: enterpriseId }
                                };
                                const resp = await DataService.client.url(`/apprenticeship/contacts/`).post(payload).json();
                                toast.success("Contact créé avec succès");
                                return json({ success: true, content: resp });
                            } catch (error) {
                                toast.error("Une erreur est survenue lors de la création du contact");
                                return json({ error });
                            }
                        }
                    },
                    children: [
                        {
                            path: ":contactId",
                            action: async ({ params, request }) => {
                                const enterpriseId = params.enterpriseId;
                                const contactId = params.contactId;
                                const data = await request.json();
                                if (request.method == "POST") {
                                    try {
                                        const payload = {
                                            ...data,
                                            company: { id: enterpriseId }
                                        };
                                        const resp = await DataService.client.url(`/apprenticeship/contacts/${contactId}/`).patch(payload).json();
                                        toast.success("Contact modifié avec succès");
                                        return json({ success: true, content: resp });
                                    } catch (error) {
                                        toast.error("Une erreur est survenue lors de la modification du contact");
                                        return json({ error });
                                    }
                                } else if (request.method == "DELETE") {
                                    try {
                                        await DataService.client.url(`/apprenticeship/contacts/${contactId}/`).delete();
                                        toast.success("Contact supprimé avec succès");
                                        return redirect(`/alternance/groupes/${enterpriseId}/contacts`);
                                    } catch (error) {
                                        toast.error("Une erreur est survenue lors de la suppression du contact");
                                        return json({ error });
                                    }
                                }
                            }
                        }
                    ]
                },
                {
                    path: "sites",
                    async lazy() {
                        let { SitesPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/detail-sites");
                        return { Component: SitesPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const subsidiaries = await DataService.client.url(`/apprenticeship/companies/?parent=${enterpriseId}&page_size=1000`).get().json();
                        return defer({ subsidiaries });
                    },
                    action: async ({ request, params }) => {
                        const enterpriseId = params.enterpriseId;
                        const data = await request.json();

                        try {
                            const content = await DataService.client
                                .url(`/apprenticeship/offers/`)
                                .post({ ...data, company: { id: enterpriseId } })
                                .json();
                            return handle_success(content);
                        } catch (error) {
                            return handle_error(error);
                        }
                    },
                    children: [
                        {
                            path: ":offerId",
                            action: async ({ params, request }) => {
                                const offerId = params.offerId;
                                const data = await request.json();
                                const url = new URL(request.url);
                                const action = url.searchParams.get("action");
                                try {
                                    let content;
                                    if (request.method == "POST") {
                                        if (action) {
                                            content = await DataService.client.url(`/apprenticeship/offers/${offerId}/${action}/`).post(data).json();
                                        } else {
                                            content = await DataService.client.url(`/apprenticeship/offers/${offerId}/`).patch(data).json();
                                        }
                                    } else if (request.method == "DELETE") {
                                        content = await DataService.client.url(`/apprenticeship/offers/${offerId}/`).delete().json();
                                    }
                                    return handle_success(content);
                                } catch (error) {
                                    return handle_error(error);
                                }
                            }
                        }
                    ]
                },
                {
                    path: "offre-active",
                    loader: async ({ params, request }) => {
                        const enterpriseId = params.enterpriseId;
                        const productIn = new URL(request.url).searchParams.get("product__in");
                        const productQuery = productIn ? `?product__in=${encodeURIComponent(productIn)}` : "";
                        const active_offer = await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/active_offer/${productQuery}`).get().json();
                        return active_offer;
                    }
                },
                {
                    path: "entretiens",
                    loader: async ({ request, params }) => {
                        const url = new URL(request.url);
                        url.searchParams.set("page_size", "1000");
                        url.searchParams.set("company_id", params.enterpriseId || "0");
                        const interviews = await DataService.client.url(`/apprenticeship/interviews/?${url.searchParams.toString()}`).get().json();
                        return interviews;
                    }
                },
                {
                    path: "carte",
                    async lazy() {
                        let { MapPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/detail-map");
                        return { Component: MapPage };
                    },
                    loader: async ({ request, params }) => {
                        const url = new URL(request.url);
                        const enterpriseId = params.enterpriseId;
                        const registrations = DataService.client
                            .url(`/apprenticeship/companies/${enterpriseId}/map_detail/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        return defer({ registrations });
                    }
                },
                {
                    path: "alternants",
                    async lazy() {
                        let { ApprenticesPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/detail-apprentices");
                        return { Component: ApprenticesPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const apprentices = DataService.client.url(`/apprenticeship/companies/${enterpriseId}/apprentices/`).get().json();
                        return defer({ apprentices });
                    }
                },
                {
                    path: "etablissements-orphelins",
                    async lazy() {
                        let { OrphanSiblingsPage } = await import("@/pages/apprenticeship/enterprise/enterprise-group/detail-orphan-siblings");
                        return { Component: OrphanSiblingsPage };
                    },
                    loader: async ({ params }) => {
                        const enterpriseId = params.enterpriseId;
                        const siblings = await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/orphan_siblings/`).get().json();
                        return { siblings };
                    },
                    action: async ({ params, request }) => {
                        const enterpriseId = params.enterpriseId;
                        const data = await request.formData();
                        const sibling_id = data.get("sibling_id");
                        await DataService.client.url(`/apprenticeship/companies/${enterpriseId}/attach_sibling/`).post({ sibling_id }).json();
                        return { attached: true };
                    }
                }
            ]
        }
    ]
};

export default EnterpriseGroupRoute;
